import { useState, useEffect } from "react";
import { Icon, PROVIDERS, api } from "../lib/core";
import { ContactsPage } from "./contacts";

export const GroupsPage = ({ onBack, dataPlans }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showBulkRechargeModal, setShowBulkRechargeModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showGroupTransactions, setShowGroupTransactions] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await api.get("/groups");
      setGroups(response.data);
    } catch (err) {
      console.error("Failed to fetch groups:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Are you sure you want to delete this group?")) return;
    
    try {
      await api.delete(`/groups/${groupId}`);
      setGroups(groups.filter(g => g.id !== groupId));
      setMessage({ type: "success", text: "Group deleted" });
    } catch (err) {
      setMessage({ type: "error", text: "Failed to delete group" });
    }
  };

  const PRESET_GROUPS = ["Family", "Church", "School", "Work", "Staff", "Community"];

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <Icon name="progress_activity" className="text-primary-custom text-4xl animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <header className="bg-primary-custom text-white sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <Icon name="arrow_back" />
          </button>
          <span className="font-bold text-xl flex-1">Beneficiaries</span>
          <button
            data-testid="create-group-btn"
            onClick={() => setShowCreateModal(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Icon name="add" />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-4 pb-24">
        {/* Message */}
        {message.text && (
          <div className={`px-4 py-3 rounded-xl flex items-center gap-2 ${
            message.type === "success" 
              ? "bg-green-50 border border-green-200 text-green-700" 
              : "bg-red-50 border border-red-200 text-red-700"
          }`}>
            <Icon name={message.type === "success" ? "check_circle" : "error"} />
            <span>{message.text}</span>
            <button onClick={() => setMessage({ type: "", text: "" })} className="ml-auto">
              <Icon name="close" className="text-sm" />
            </button>
          </div>
        )}

        {groups.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-primary-custom/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="groups" className="text-primary-custom text-4xl" />
            </div>
            <h3 className="font-bold text-slate-900 text-lg">No Beneficiaries Yet</h3>
            <p className="text-slate-500 mt-2">Save contacts as beneficiaries so you can recharge them in seconds — individually or in bulk.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-6 px-6 py-3 bg-primary-custom text-white rounded-xl font-semibold flex items-center gap-2 mx-auto"
            >
              <Icon name="add" />
              <span>Add First Beneficiary</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div
                key={group.id}
                data-testid={`group-${group.id}`}
                className="bg-white rounded-2xl overflow-hidden border border-slate-200"
              >
                {/* Group Header */}
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary-custom/10 rounded-xl flex items-center justify-center">
                        <Icon name="groups" className="text-primary-custom text-xl" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{group.name}</h3>
                        <p className="text-sm text-slate-500">{group.member_count} members</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteGroup(group.id)}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Icon name="delete" />
                    </button>
                  </div>
                  
                  {/* Recurring Badge */}
                  {group.recurring_schedule && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <Icon name={group.recurring_schedule.type === "auto" ? "autorenew" : "notifications"} className="text-primary-custom" />
                      <span className="text-slate-600">
                        {group.recurring_schedule.type === "auto" ? "Auto-recharge" : "Reminder"}: {group.recurring_schedule.frequency}
                      </span>
                    </div>
                  )}
                </div>

                {/* Members Preview */}
                {group.members.length > 0 && (
                  <div className="p-4 bg-slate-50 border-b border-slate-100">
                    <div className="flex flex-wrap gap-2">
                      {group.members.slice(0, 5).map((member) => (
                        <span
                          key={member.id}
                          className="px-3 py-1 bg-white rounded-full text-sm text-slate-700 border border-slate-200"
                        >
                          {member.nickname}
                        </span>
                      ))}
                      {group.members.length > 5 && (
                        <span className="px-3 py-1 bg-slate-200 rounded-full text-sm text-slate-600">
                          +{group.members.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Group Actions */}
                <div className="p-3 flex gap-2">
                  <button
                    data-testid={`add-member-${group.id}`}
                    onClick={() => { setSelectedGroup(group); setShowAddMemberModal(true); }}
                    className="flex-1 py-2.5 px-3 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Icon name="person_add" className="text-base" />
                    <span>Add</span>
                  </button>
                  <button
                    data-testid={`bulk-recharge-${group.id}`}
                    onClick={() => { setSelectedGroup(group); setSelectedMembers([]); setShowBulkRechargeModal(true); }}
                    disabled={group.member_count === 0}
                    className="flex-1 py-2.5 px-3 bg-primary-custom hover:bg-primary-custom/90 rounded-lg text-sm font-medium text-white flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    <Icon name="bolt" className="text-base" />
                    <span>Recharge</span>
                  </button>
                  <button
                    data-testid={`recurring-${group.id}`}
                    onClick={() => { setSelectedGroup(group); setShowRecurringModal(true); }}
                    className="py-2.5 px-3 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 flex items-center justify-center transition-colors"
                  >
                    <Icon name="schedule" className="text-base" />
                  </button>
                  <button
                    data-testid={`transactions-${group.id}`}
                    onClick={() => { setSelectedGroup(group); setShowGroupTransactions(true); }}
                    className="py-2.5 px-3 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 flex items-center justify-center transition-colors"
                  >
                    <Icon name="receipt_long" className="text-base" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Group Modal */}
      {showCreateModal && (
        <CreateGroupModal
          presetGroups={PRESET_GROUPS}
          onClose={() => setShowCreateModal(false)}
          onCreated={(newGroup) => {
            setGroups([...groups, newGroup]);
            setShowCreateModal(false);
            setMessage({ type: "success", text: "Group created!" });
          }}
        />
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && selectedGroup && (
        <AddMemberModal
          group={selectedGroup}
          onClose={() => { setShowAddMemberModal(false); setSelectedGroup(null); }}
          onMemberAdded={(updatedGroup) => {
            setGroups(groups.map(g => g.id === updatedGroup.id ? updatedGroup : g));
            setShowAddMemberModal(false);
            setSelectedGroup(null);
            setMessage({ type: "success", text: "Member added!" });
          }}
          dataPlans={dataPlans}
        />
      )}

      {/* Bulk Recharge Modal */}
      {showBulkRechargeModal && selectedGroup && (
        <BulkRechargeModal
          group={selectedGroup}
          onClose={() => { setShowBulkRechargeModal(false); setSelectedGroup(null); setSelectedMembers([]); }}
          onSuccess={(result) => {
            setShowBulkRechargeModal(false);
            setSelectedGroup(null);
            setSelectedMembers([]);
            setMessage({ type: "success", text: `Recharged ${result.successful}/${result.total_members} members` });
          }}
          dataPlans={dataPlans}
        />
      )}

      {/* Recurring Schedule Modal */}
      {showRecurringModal && selectedGroup && (
        <RecurringScheduleModal
          group={selectedGroup}
          onClose={() => { setShowRecurringModal(false); setSelectedGroup(null); }}
          onSaved={(updatedGroup) => {
            setGroups(groups.map(g => g.id === updatedGroup.id ? updatedGroup : g));
            setShowRecurringModal(false);
            setSelectedGroup(null);
            setMessage({ type: "success", text: "Schedule saved!" });
          }}
          dataPlans={dataPlans}
        />
      )}

      {/* Group Transactions Modal */}
      {showGroupTransactions && selectedGroup && (
        <GroupTransactionsModal
          group={selectedGroup}
          onClose={() => { setShowGroupTransactions(false); setSelectedGroup(null); }}
        />
      )}
    </div>
  );
};

// Create Group Modal Component

export const CreateGroupModal = ({ presetGroups, onClose, onCreated }) => {
  const [groupName, setGroupName] = useState("");
  const [customName, setCustomName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    const name = groupName === "custom" ? customName : groupName;
    if (!name) return;

    setLoading(true);
    try {
      const response = await api.post("/groups", { name, members: [] });
      onCreated(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create group");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-xl text-slate-900">Create Group</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <Icon name="close" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <p className="text-slate-600 text-sm">Select a group type or create a custom one:</p>
          
          <div className="grid grid-cols-2 gap-3">
            {presetGroups.map((name) => (
              <button
                key={name}
                onClick={() => setGroupName(name)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  groupName === name
                    ? "border-primary-custom bg-primary-custom/5"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <Icon name={name === "Family" ? "family_restroom" : name === "Church" ? "church" : name === "School" ? "school" : name === "Work" ? "work" : name === "Staff" ? "badge" : "groups"} className="text-primary-custom text-2xl" />
                <p className="font-semibold text-slate-900 mt-2">{name}</p>
              </button>
            ))}
            <button
              onClick={() => setGroupName("custom")}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                groupName === "custom"
                  ? "border-primary-custom bg-primary-custom/5"
                  : "border-slate-200 hover:border-slate-300 border-dashed"
              }`}
            >
              <Icon name="add_circle" className="text-primary-custom text-2xl" />
              <p className="font-semibold text-slate-900 mt-2">Custom</p>
            </button>
          </div>

          {groupName === "custom" && (
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Enter custom group name"
              className="w-full h-14 px-4 rounded-xl border border-slate-200 focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 outline-none"
            />
          )}

          <button
            onClick={handleCreate}
            disabled={loading || (!groupName || (groupName === "custom" && !customName))}
            className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="add" />}
            <span>Create Group</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Member Modal Component

export const AddMemberModal = ({ group, onClose, onMemberAdded, dataPlans }) => {
  const [phone, setPhone] = useState("");
  const [nickname, setNickname] = useState("");
  const [provider, setProvider] = useState("");
  const [bundleType, setBundleType] = useState("airtime");
  const [selectedSubcode, setSelectedSubcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const providerPlans = dataPlans[provider] || [];

  const handleAdd = async () => {
    if (!phone || !nickname || !provider) return;

    setLoading(true);
    try {
      const response = await api.post(`/groups/${group.id}/members`, {
        phone,
        nickname,
        provider,
        preferred_bundle_type: bundleType,
        preferred_subcode: bundleType === "data" ? selectedSubcode : null
      });
      onMemberAdded(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add member");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-xl text-slate-900">Add Member</h2>
            <p className="text-sm text-slate-500">to {group.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <Icon name="close" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <button type="button" onClick={() => setShowPicker(true)} data-testid="beneficiary-pick-contact"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-primary-custom/40 text-primary-custom font-semibold text-sm hover:bg-primary-custom/5 transition-colors">
            <Icon name="contacts" className="text-base" /> Pick from Contacts
          </button>
          <div>
            <label className="text-slate-700 text-sm font-semibold">Nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="e.g., Mom, John, Office"
              className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
            />
          </div>

          <div>
            <label className="text-slate-700 text-sm font-semibold">Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="08012345678"
              className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
            />
          </div>

          <div>
            <label className="text-slate-700 text-sm font-semibold">Network Provider</label>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              {PROVIDERS.map((p) => {
                const scale = p.logoScale || 1;
                const logoSize = Math.round(24 * scale);
                return (
                  <button
                    key={p.name}
                    onClick={() => setProvider(p.name)}
                    className={`p-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                      provider === p.name ? "border-primary-custom" : "border-slate-200"
                    }`}
                    style={{ backgroundColor: provider === p.name ? p.color : "white" }}
                  >
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center overflow-hidden">
                      <img src={p.logo} alt={p.name} className="object-contain" style={{ width: `${logoSize}px`, height: `${logoSize}px` }} />
                    </div>
                    <span className={`text-xs font-bold ${provider === p.name ? "text-white" : "text-slate-700"}`} style={{ color: provider === p.name ? p.textColor : undefined }}>
                      {p.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-slate-700 text-sm font-semibold">Preferred Bundle Type</label>
            <div className="flex gap-2 mt-1.5">
              <button
                onClick={() => setBundleType("airtime")}
                className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                  bundleType === "airtime" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                }`}
              >
                Airtime
              </button>
              <button
                onClick={() => setBundleType("data")}
                className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                  bundleType === "data" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                }`}
              >
                Data
              </button>
            </div>
          </div>

          {bundleType === "data" && provider && providerPlans.length > 0 && (
            <div>
              <label className="text-slate-700 text-sm font-semibold">Preferred Data Plan</label>
              <select
                value={selectedSubcode}
                onChange={(e) => setSelectedSubcode(e.target.value)}
                className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
              >
                <option value="">Select a plan</option>
                {providerPlans.map((plan) => (
                  <option key={plan.subCode} value={plan.subCode}>
                    {plan.subCode} - ₦{plan.amount.toLocaleString()} - {plan.description}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            onClick={handleAdd}
            disabled={loading || !phone || !nickname || !provider}
            className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="person_add" />}
            <span>Add Member</span>
          </button>
        </div>
      </div>

      {showPicker && (
        <div className="fixed inset-0 z-[90] overflow-y-auto bg-background-light" data-testid="beneficiary-contact-picker">
          <ContactsPage
            pickMode
            onBack={() => setShowPicker(false)}
            onPick={(c) => {
              setNickname(c.name || "");
              setPhone(c.phone || "");
              if (c.provider) setProvider(c.provider);
              setShowPicker(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

// Bulk Recharge Modal Component

export const BulkRechargeModal = ({ group, onClose, onSuccess, dataPlans }) => {
  const [selectedMembers, setSelectedMembers] = useState(group.members.map(m => m.id));
  const [rechargeAll, setRechargeAll] = useState(true);
  const [amount, setAmount] = useState("");
  const [bundleType, setBundleType] = useState("airtime");
  const [selectedSubcode, setSelectedSubcode] = useState("");
  const [useMemberPreferences, setUseMemberPreferences] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleMember = (memberId) => {
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };

  const handleRecharge = async () => {
    if (!amount || selectedMembers.length === 0) return;

    setLoading(true);
    try {
      const endpoint = rechargeAll ? `/groups/${group.id}/recharge` : `/groups/${group.id}/recharge-selected`;
      const payload = {
        amount: parseFloat(amount),
        bundle_type: bundleType,
        subcode: bundleType === "data" ? selectedSubcode : null,
        use_member_preferences: useMemberPreferences
      };

      if (!rechargeAll) {
        payload.member_ids = selectedMembers;
      }

      const response = await api.post(endpoint, payload);
      onSuccess(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Recharge failed");
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = (parseFloat(amount) || 0) * (rechargeAll ? group.members.length : selectedMembers.length);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-xl text-slate-900">Bulk Recharge</h2>
            <p className="text-sm text-slate-500">{group.name} • {group.member_count} members</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <Icon name="close" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Recharge Type */}
          <div className="flex gap-2">
            <button
              onClick={() => { setRechargeAll(true); setSelectedMembers(group.members.map(m => m.id)); }}
              className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                rechargeAll ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              All Members
            </button>
            <button
              onClick={() => setRechargeAll(false)}
              className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                !rechargeAll ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              Select Members
            </button>
          </div>

          {/* Member Selection */}
          {!rechargeAll && (
            <div className="max-h-48 overflow-y-auto space-y-2 p-3 bg-slate-50 rounded-xl">
              {group.members.map((member) => (
                <label key={member.id} className="flex items-center gap-3 p-2 bg-white rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => toggleMember(member.id)}
                    className="w-5 h-5 rounded text-primary-custom"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{member.nickname}</p>
                    <p className="text-xs text-slate-500">{member.phone} • {member.provider}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Use Member Preferences */}
          <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={useMemberPreferences}
              onChange={(e) => setUseMemberPreferences(e.target.checked)}
              className="w-5 h-5 rounded text-primary-custom"
            />
            <div>
              <p className="font-medium text-slate-900">Use member preferences</p>
              <p className="text-xs text-slate-500">Each member gets their preferred bundle type</p>
            </div>
          </label>

          {/* Bundle Type (if not using preferences) */}
          {!useMemberPreferences && (
            <div className="flex gap-2">
              <button
                onClick={() => setBundleType("airtime")}
                className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                  bundleType === "airtime" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                }`}
              >
                Airtime
              </button>
              <button
                onClick={() => setBundleType("data")}
                className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                  bundleType === "data" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                }`}
              >
                Data
              </button>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="text-slate-700 text-sm font-semibold">Amount per member</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full h-14 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none text-xl font-bold text-center"
            />
            <div className="flex gap-2 mt-2">
              {[100, 200, 500, 1000].map((amt) => (
                <button
                  key={amt}
                  onClick={() => setAmount(amt.toString())}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                    amount === amt.toString() ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  ₦{amt}
                </button>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="p-4 bg-primary-custom/10 rounded-xl">
            <div className="flex justify-between items-center">
              <span className="text-slate-700">Total Amount</span>
              <span className="text-2xl font-bold text-primary-custom">₦{totalAmount.toLocaleString()}</span>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {rechargeAll ? group.members.length : selectedMembers.length} members × ₦{amount || 0}
            </p>
          </div>

          <button
            onClick={handleRecharge}
            disabled={loading || !amount || selectedMembers.length === 0}
            className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="bolt" />}
            <span>Recharge Now</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Recurring Schedule Modal Component

export const RecurringScheduleModal = ({ group, onClose, onSaved, dataPlans }) => {
  const existingSchedule = group.recurring_schedule;
  const [scheduleType, setScheduleType] = useState(existingSchedule?.type || "reminder");
  const [frequency, setFrequency] = useState(existingSchedule?.frequency || "weekly");
  const [dayOfWeek, setDayOfWeek] = useState(existingSchedule?.day_of_week ?? 0);
  const [dayOfMonth, setDayOfMonth] = useState(existingSchedule?.day_of_month ?? 1);
  const [timeOfDay, setTimeOfDay] = useState(existingSchedule?.time_of_day || "09:00");
  const [amount, setAmount] = useState(existingSchedule?.amount?.toString() || "");
  const [bundleType, setBundleType] = useState(existingSchedule?.bundle_type || "airtime");
  const [loading, setLoading] = useState(false);

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const handleSave = async () => {
    if (!amount) return;

    setLoading(true);
    try {
      const response = await api.put(`/groups/${group.id}/recurring`, {
        type: scheduleType,
        frequency,
        day_of_week: frequency === "weekly" ? dayOfWeek : null,
        day_of_month: frequency === "monthly" ? dayOfMonth : null,
        time_of_day: timeOfDay,
        amount: parseFloat(amount),
        bundle_type: bundleType,
        is_active: true
      });
      onSaved(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save schedule");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove recurring schedule?")) return;

    try {
      await api.delete(`/groups/${group.id}/recurring`);
      onSaved({ ...group, recurring_schedule: null });
    } catch (err) {
      alert("Failed to remove schedule");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-xl text-slate-900">Recurring Recharge</h2>
            <p className="text-sm text-slate-500">{group.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <Icon name="close" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Schedule Type */}
          <div>
            <label className="text-slate-700 text-sm font-semibold">Schedule Type</label>
            <div className="flex gap-2 mt-1.5">
              <button
                onClick={() => setScheduleType("reminder")}
                className={`flex-1 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                  scheduleType === "reminder" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                }`}
              >
                <Icon name="notifications" />
                <span>Reminder</span>
              </button>
              <button
                onClick={() => setScheduleType("auto")}
                className={`flex-1 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                  scheduleType === "auto" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                }`}
              >
                <Icon name="autorenew" />
                <span>Auto</span>
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {scheduleType === "reminder" 
                ? "You'll receive a notification to recharge" 
                : "Recharge will happen automatically"}
            </p>
          </div>

          {/* Frequency */}
          <div>
            <label className="text-slate-700 text-sm font-semibold">Frequency</label>
            <div className="flex gap-2 mt-1.5">
              {["daily", "weekly", "monthly"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFrequency(f)}
                  className={`flex-1 py-2.5 rounded-xl font-medium capitalize transition-all ${
                    frequency === f ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Day Selection */}
          {frequency === "weekly" && (
            <div>
              <label className="text-slate-700 text-sm font-semibold">Day of Week</label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
                className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
              >
                {DAYS.map((day, i) => (
                  <option key={day} value={i}>{day}</option>
                ))}
              </select>
            </div>
          )}

          {frequency === "monthly" && (
            <div>
              <label className="text-slate-700 text-sm font-semibold">Day of Month</label>
              <select
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
                className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
              >
                {[...Array(28)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </div>
          )}

          {/* Time */}
          <div>
            <label className="text-slate-700 text-sm font-semibold">Time</label>
            <input
              type="time"
              value={timeOfDay}
              onChange={(e) => setTimeOfDay(e.target.value)}
              className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="text-slate-700 text-sm font-semibold">Amount per member</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none text-lg font-bold"
            />
          </div>

          {/* Bundle Type */}
          <div className="flex gap-2">
            <button
              onClick={() => setBundleType("airtime")}
              className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                bundleType === "airtime" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              Airtime
            </button>
            <button
              onClick={() => setBundleType("data")}
              className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                bundleType === "data" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              Data
            </button>
          </div>

          <button
            onClick={handleSave}
            disabled={loading || !amount}
            className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="save" />}
            <span>Save Schedule</span>
          </button>

          {existingSchedule && (
            <button
              onClick={handleDelete}
              className="w-full h-12 text-red-500 hover:bg-red-50 rounded-xl font-medium flex items-center justify-center gap-2"
            >
              <Icon name="delete" />
              <span>Remove Schedule</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Group Transactions Modal Component

export const GroupTransactionsModal = ({ group, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await api.get(`/groups/${group.id}/transactions`);
      setData(response.data);
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-xl text-slate-900">Transaction History</h2>
            <p className="text-sm text-slate-500">{group.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <Icon name="close" />
          </button>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Icon name="progress_activity" className="text-primary-custom text-4xl animate-spin" />
            </div>
          ) : data ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-primary-custom/10 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary-custom">₦{data.summary.total_spent.toLocaleString()}</p>
                  <p className="text-xs text-slate-600">Total Spent</p>
                </div>
                <div className="bg-slate-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{data.summary.total_recharges}</p>
                  <p className="text-xs text-slate-600">Recharges</p>
                </div>
                <div className="bg-slate-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{data.summary.member_count}</p>
                  <p className="text-xs text-slate-600">Members</p>
                </div>
              </div>

              {/* Transaction List */}
              {data.transactions.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Icon name="receipt_long" className="text-4xl" />
                  <p className="mt-2">No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.transactions.map((tx) => (
                    <div key={tx.id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-900 capitalize">{tx.type} Recharge</p>
                          <p className="text-xs text-slate-500">
                            {new Date(tx.timestamp).toLocaleDateString()} • {tx.successful}/{tx.total_members} successful
                          </p>
                        </div>
                        <p className="font-bold text-primary-custom">₦{tx.total_amount.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <p>Failed to load transactions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Wallet transaction row
